import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { recalcAll, getDefaultHsn, getEffectiveGstRate } from '@/lib/gstHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateProformaInvoice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [nextPINo, setNextPINo] = useState('');
  const [districts, setDistricts] = useState([]);
  const [lastPINumber, setLastPINumber] = useState({ aabkari: 'None', sugar: 'None' });
  const [isCopiedData, setIsCopiedData] = useState(false);
  
  // Helper function to get current month name
  const getCurrentMonth = () => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months[new Date().getMonth()];
  };

  // Helper function to get current year
  const getCurrentYear = () => {
    return new Date().getFullYear().toString();
  };
  
  const [formData, setFormData] = useState({
    fy: '2026-27',
    document_type: 'Proforma Invoice',
    magazine: '',
    type: '',
    product: '',
    po_wo_no: '',
    proforma_invoice_no: '',
    entity_id: '',
    initial: 'M/s.',
    billing_name: '',
    bill_receipt_name: '',
    billing_address: '',
    billing_state: '',
    district: '',
    pincode: '',
    gst_no: '',
    occasion: '',
    bill_date: new Date().toISOString().split('T')[0],
    start_month: '',
    start_year: '',
    end_month: '',
    end_year: '',
    advertisement_position: '',
    advertisement_tenure: '',
    item_description: '',
    include_in_aabkari: false,
    gross_amount: '',
    discount_percentage: '',
    discount_amount: '',
    sgst_amount: 0,
    cgst_amount: 0,
    igst_amount: 0,
    total_amount: '',
    gst_rate: 'auto',           // 'auto' | '0' | '5' | '18'
    hsn_sac: '',                // HSN/SAC code (defaults from product)
    last_edited_field: '',      // '' | 'gross' | 'discount' | 'total' | 'gst_rate'
    bill_no: '',
    terms_conditions: '1. Artwork approval required before printing for multiple issues.\n2. Post-order confirmation, PO with full terms should be provided.\n3. Dimensions: Full Page Size of 7.25" x 10.5" (A4)\n4. Supported file formats: .jpeg, .jpg, .pdf, .cdr (CDR v.17 or below)\n5. 1 print copy shall be sent at your registered postal address.'
  });

  // Track whether the user has manually edited the description.
  // Once true, useEffect MUST stop overwriting it.
  // Lazy initializer: when navigating from Copy, mark as edited on the very
  // first render so the auto-fill effect never overwrites the copied text.
  const [userEditedDescription, setUserEditedDescription] = useState(
    () => !!location.state?.copyFrom?.item_description
  );

  // Number to words converter
  const numberToWords = (num) => {
    if (num === 0) return 'Zero Rupees Only';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
    };
    
    const convert = (n) => {
      if (n === 0) return '';
      
      let result = '';
      
      // Crores
      if (n >= 10000000) {
        result += convertLessThanThousand(Math.floor(n / 10000000)) + ' Crore ';
        n %= 10000000;
      }
      
      // Lakhs
      if (n >= 100000) {
        result += convertLessThanThousand(Math.floor(n / 100000)) + ' Lakh ';
        n %= 100000;
      }
      
      // Thousands
      if (n >= 1000) {
        result += convertLessThanThousand(Math.floor(n / 1000)) + ' Thousand ';
        n %= 1000;
      }
      
      // Hundreds and below
      if (n > 0) {
        result += convertLessThanThousand(n);
      }
      
      return result.trim();
    };
    
    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);
    
    let words = convert(rupees) + ' Rupees';
    if (paise > 0) {
      words += ' and ' + convert(paise) + ' Paise';
    }
    words += ' Only';
    
    return words;
  };

  useEffect(() => {
    fetchLastPINumbers();
  }, []);

  // Pre-fill form when navigating from Copy button
  useEffect(() => {
    if (location.state?.copyFrom) {
      const copied = location.state.copyFrom;
      setIsCopiedData(true);
      // Mark description as user-edited so the auto-fill effect does not overwrite
      // the copied item_description.
      if (copied.item_description) {
        setUserEditedDescription(true);
      }
      setFormData(prev => ({
        ...prev,
        ...copied,
        proforma_invoice_no: '',
        bill_no: '',
        bill_date: new Date().toISOString().split('T')[0],
      }));
      toast.info('Data copied. A new PI number will be assigned automatically.');
    }
  }, []);

  useEffect(() => {
    if (formData.magazine && formData.fy) {
      fetchNextPINumber();
    }
  }, [formData.magazine, formData.fy]);

  // Sync proforma_invoice_no with nextPINo when nextPINo changes
  useEffect(() => {
    if (nextPINo && !formData.proforma_invoice_no) {
      setFormData(prev => ({ ...prev, proforma_invoice_no: nextPINo, bill_no: nextPINo }));
    }
  }, [nextPINo]);

  // Fetch districts when billing state changes
  const fetchDistricts = async (state) => {
    if (!state) {
      setDistricts([]);
      return;
    }
    try {
      let response;
      const token = localStorage.getItem('token');
      try {
        response = await axios.get(`${API}/members/unique-districts/${state}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch {
        response = await axios.get(`${API}/advertisers/unique-districts/${state}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    }
  };

  const fetchLastPINumbers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/bills/last-pi-numbers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLastPINumber(response.data);
    } catch (error) {
      console.error('Error fetching last PI numbers:', error);
    }
  };

  const fetchNextPINumber = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/bills/next-pi-number`, {
        params: { magazine: formData.magazine, fy: formData.fy },
        headers: { Authorization: `Bearer ${token}` }
      });
      setNextPINo(response.data.next_pi_no);
      setFormData(prev => ({ ...prev, proforma_invoice_no: response.data.next_pi_no, bill_no: response.data.next_pi_no }));
    } catch (error) {
      console.error('Error fetching next PI number:', error);
    }
  };

  // Fetch advertiser details when entity_id changes (for auto-fill)
  useEffect(() => {
    const fetchAdvertiserDetails = async () => {
      if (formData.type === 'Advertiser' && formData.entity_id && formData.entity_id.length >= 4) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/advertisers/${formData.entity_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data) {
            setFormData(prev => ({
              ...prev,
              billing_name: response.data.company_name || prev.billing_name,
              billing_address: response.data.address || prev.billing_address,
              billing_state: response.data.state || prev.billing_state,
              district: response.data.district || prev.district,
              pincode: response.data.pincode || prev.pincode,
              gst_no: response.data.gst_no || prev.gst_no
            }));
            if (response.data.state) {
              fetchDistricts(response.data.state);
            }
          }
        } catch (error) {
          // Advertiser not found - that's ok
        }
      }
    };
    fetchAdvertiserDetails();
  }, [formData.entity_id, formData.type]);

  // Auto-populate item description with default text (only until user edits it manually)
  useEffect(() => {
    if (userEditedDescription) return; // stop overwriting once user has typed
    const defaultDesc = `Towards your ${formData.product || 'Subscription'} charges of ${formData.magazine ? formData.magazine + ' magazine' : 'Magazine'} for ${formData.advertisement_tenure || '1 Month'}`;
    setFormData(prev => ({ ...prev, item_description: defaultDesc }));
  }, [formData.product, formData.magazine, formData.advertisement_tenure, userEditedDescription]);

  // Auto-populate HSN/SAC default whenever product changes (only if blank or untouched).
  useEffect(() => {
    const def = getDefaultHsn(formData.product);
    if (!formData.hsn_sac || formData.hsn_sac === '998363' || formData.hsn_sac === '998397') {
      setFormData(prev => ({ ...prev, hsn_sac: def }));
    }
  }, [formData.product]);

  // Fetch member details when entity_id changes (for auto-fill)
  useEffect(() => {
    const fetchMemberDetails = async () => {
      if (formData.type === 'Member' && formData.entity_id && formData.entity_id.length >= 4) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/members/${formData.entity_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data) {
            setFormData(prev => ({
              ...prev,
              billing_name: response.data.company_name || response.data.name || prev.billing_name,
              billing_address: response.data.address || prev.billing_address,
              billing_state: response.data.state || prev.billing_state,
              district: response.data.district || prev.district,
              pincode: response.data.pincode || prev.pincode
            }));
            if (response.data.state) {
              fetchDistricts(response.data.state);
            }
          }
        } catch (error) {
          // Member not found - that's ok
        }
      }
    };
    fetchMemberDetails();
  }, [formData.entity_id, formData.type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.magazine || !formData.type || !formData.billing_name || !formData.gross_amount) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!formData.proforma_invoice_no) {
      toast.error('Please enter or select a Proforma Invoice number');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Run final bidirectional recalc using gstHelper so amounts are correct
      // regardless of which field the user edited last.
      const finalState = recalcAll({
        ...formData,
        gross_amount: parseFloat(formData.gross_amount) || 0,
        discount_amount: parseFloat(formData.discount_amount) || 0,
        discount_percentage: parseFloat(formData.discount_percentage) || 0,
        total_amount: parseFloat(formData.total_amount) || 0,
      });

      const payload = {
        ...formData,
        bill_no: formData.proforma_invoice_no,
        gross_amount: finalState.gross_amount,
        discount_percentage: finalState.discount_percentage,
        discount_amount: finalState.discount_amount,
        sgst_amount: finalState.sgst_amount,
        cgst_amount: finalState.cgst_amount,
        igst_amount: finalState.igst_amount,
        total_amount: finalState.total_amount,
        gst_rate: formData.gst_rate || 'auto',
        hsn_sac: formData.hsn_sac || '',
        amount_in_words: numberToWords(finalState.total_amount),
      };

      await axios.post(`${API}/bills`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Proforma Invoice ${formData.proforma_invoice_no} created successfully!`);
      navigate('/proforma-invoice-reports');
    } catch (error) {
      console.error('Error creating PI:', error);
      toast.error('Failed to create Proforma Invoice: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-8">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#059669' }} data-testid="page-title">
              Create Proforma Invoice
            </h1>
          </div>
          {nextPINo && (
            <div className="text-xl font-semibold" style={{ color: '#059669' }}>
              Next PI No: {nextPINo}
            </div>
          )}
        </div>

        {/* Last PI Numbers Info */}
        <div className="form-section mb-6">
          <h3 className="font-bold mb-2">Last PI Numbers:</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-purple-50 p-3 rounded border border-purple-200">
              <p className="text-sm text-gray-600">Aabkari Times</p>
              <p className="text-lg font-bold" style={{ color: '#7c3aed' }}>{lastPINumber.aabkari}</p>
            </div>
            <div className="bg-green-50 p-3 rounded border border-green-200">
              <p className="text-sm text-gray-600">Sugar Times</p>
              <p className="text-lg font-bold" style={{ color: '#059669' }}>{lastPINumber.sugar}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="form-section">
          {/* Document Type indicator */}
          <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
            <Label className="text-lg font-semibold" style={{ color: '#059669' }}>Document Type: Proforma Invoice</Label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Financial Year *</Label>
              <Select value={formData.fy} onValueChange={(value) => setFormData({ ...formData, fy: value })}>
                <SelectTrigger data-testid="fy-select">
                  <SelectValue placeholder="Select FY" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2026-27">2026-27</SelectItem>
                  <SelectItem value="2025-26">2025-26</SelectItem>
                  <SelectItem value="2024-25">2024-25</SelectItem>
                  <SelectItem value="2023-24">2023-24</SelectItem>
                  <SelectItem value="2022-23">2022-23</SelectItem>
                  <SelectItem value="2021-22">2021-22</SelectItem>
                  <SelectItem value="2020-21">2020-21</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Magazine *</Label>
              <Select value={formData.magazine} onValueChange={(value) => setFormData({ ...formData, magazine: value })}>
                <SelectTrigger data-testid="magazine-select">
                  <SelectValue placeholder="Select magazine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                  <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Type *</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                <SelectTrigger data-testid="type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Member">Member</SelectItem>
                  <SelectItem value="Advertiser">Advertiser</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Product</Label>
              <Select value={formData.product} onValueChange={(value) => setFormData({ ...formData, product: value })}>
                <SelectTrigger data-testid="product-select">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Subscription">Subscription</SelectItem>
                  <SelectItem value="Renewal">Renewal</SelectItem>
                  <SelectItem value="Advertisement">Advertisement</SelectItem>
                  <SelectItem value="Subscription/Advertisement">Subscription/Advertisement</SelectItem>
                  <SelectItem value="Event">Event</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>PI No (editable) *</Label>
              <Input 
                value={formData.proforma_invoice_no} 
                onChange={(e) => setFormData({ ...formData, proforma_invoice_no: e.target.value, bill_no: e.target.value })} 
                placeholder="eg. ATPI-001" 
              />
              {nextPINo && formData.proforma_invoice_no !== nextPINo && (
                <p className="text-xs text-gray-500">Suggested: {nextPINo}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>PI Date *</Label>
              <Input 
                type="date"
                value={formData.bill_date} 
                onChange={(e) => setFormData({ ...formData, bill_date: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>P.O./W.O. No. (if any)</Label>
              <Input value={formData.po_wo_no} onChange={(e) => setFormData({ ...formData, po_wo_no: e.target.value })} placeholder="Enter P.O./W.O. No." />
            </div>

            <div className="space-y-2">
              <Label>Id</Label>
              <Input value={formData.entity_id} onChange={(e) => setFormData({ ...formData, entity_id: e.target.value })} placeholder="eg. ATMR12 or ATAD05" />
              {formData.type && (
                <p className="text-xs text-gray-500">Enter {formData.type === 'Advertiser' ? 'Advertiser' : 'Member'} ID to auto-fill details</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Initial *</Label>
              <Select value={formData.initial} onValueChange={(value) => setFormData({ ...formData, initial: value })}>
                <SelectTrigger data-testid="initial-select">
                  <SelectValue placeholder="Select initial" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Mr.">Mr.</SelectItem>
                  <SelectItem value="Mrs.">Mrs.</SelectItem>
                  <SelectItem value="Ms.">Ms.</SelectItem>
                  <SelectItem value="Dr.">Dr.</SelectItem>
                  <SelectItem value="M/s.">M/s.</SelectItem>
                  <SelectItem value="The">The</SelectItem>
                  <SelectItem value="To">To</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Billing Name *</Label>
              <Input 
                value={formData.billing_name} 
                onChange={(e) => setFormData({ ...formData, billing_name: e.target.value })} 
                placeholder={formData.type ? `Auto-filled from ${formData.type.toLowerCase()} (editable)` : 'Enter billing name'} 
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Bill/Receipt in Name of</Label>
              <Input value={formData.bill_receipt_name} onChange={(e) => setFormData({ ...formData, bill_receipt_name: e.target.value })} placeholder="Enter name" />
            </div>

            <div className="space-y-2">
              <Label>GST Number (Optional)</Label>
              <Input value={formData.gst_no} onChange={(e) => setFormData({ ...formData, gst_no: e.target.value })} placeholder="Enter GST number" />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Billing Address</Label>
              <Textarea 
                value={formData.billing_address} 
                onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })} 
                placeholder={formData.type ? `Auto-filled from ${formData.type.toLowerCase()} (editable)` : 'Enter billing address'}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Billing State *</Label>
              <Select 
                value={formData.billing_state} 
                onValueChange={(value) => {
                  setFormData({ ...formData, billing_state: value, district: '' });
                  fetchDistricts(value);
                }}
              >
                <SelectTrigger data-testid="billing-state-select">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Andhra Pradesh">Andhra Pradesh</SelectItem>
                  <SelectItem value="Arunachal Pradesh">Arunachal Pradesh</SelectItem>
                  <SelectItem value="Assam">Assam</SelectItem>
                  <SelectItem value="Bihar">Bihar</SelectItem>
                  <SelectItem value="Chhattisgarh">Chhattisgarh</SelectItem>
                  <SelectItem value="Delhi">Delhi</SelectItem>
                  <SelectItem value="Goa">Goa</SelectItem>
                  <SelectItem value="Gujarat">Gujarat</SelectItem>
                  <SelectItem value="Haryana">Haryana</SelectItem>
                  <SelectItem value="Himachal Pradesh">Himachal Pradesh</SelectItem>
                  <SelectItem value="Jharkhand">Jharkhand</SelectItem>
                  <SelectItem value="Karnataka">Karnataka</SelectItem>
                  <SelectItem value="Kerala">Kerala</SelectItem>
                  <SelectItem value="Madhya Pradesh">Madhya Pradesh</SelectItem>
                  <SelectItem value="Maharashtra">Maharashtra</SelectItem>
                  <SelectItem value="Manipur">Manipur</SelectItem>
                  <SelectItem value="Meghalaya">Meghalaya</SelectItem>
                  <SelectItem value="Mizoram">Mizoram</SelectItem>
                  <SelectItem value="Nagaland">Nagaland</SelectItem>
                  <SelectItem value="Odisha">Odisha</SelectItem>
                  <SelectItem value="Punjab">Punjab</SelectItem>
                  <SelectItem value="Rajasthan">Rajasthan</SelectItem>
                  <SelectItem value="Sikkim">Sikkim</SelectItem>
                  <SelectItem value="Tamil Nadu">Tamil Nadu</SelectItem>
                  <SelectItem value="Telangana">Telangana</SelectItem>
                  <SelectItem value="Tripura">Tripura</SelectItem>
                  <SelectItem value="Uttar Pradesh">Uttar Pradesh</SelectItem>
                  <SelectItem value="Uttarakhand">Uttarakhand</SelectItem>
                  <SelectItem value="West Bengal">West Bengal</SelectItem>
                  <SelectItem value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</SelectItem>
                  <SelectItem value="Chandigarh">Chandigarh</SelectItem>
                  <SelectItem value="Dadra and Nagar Haveli and Daman and Diu">Dadra and Nagar Haveli and Daman and Diu</SelectItem>
                  <SelectItem value="Jammu and Kashmir">Jammu and Kashmir</SelectItem>
                  <SelectItem value="Ladakh">Ladakh</SelectItem>
                  <SelectItem value="Lakshadweep">Lakshadweep</SelectItem>
                  <SelectItem value="Puducherry">Puducherry</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>District</Label>
              <Input 
                list="districts-list"
                value={formData.district} 
                onChange={(e) => setFormData({ ...formData, district: e.target.value })} 
                placeholder="Select or type district"
              />
              <datalist id="districts-list">
                {districts.map((district) => (
                  <option key={district} value={district} />
                ))}
              </datalist>
            </div>

            <div className="space-y-2">
              <Label>Pincode</Label>
              <Input 
                value={formData.pincode} 
                onChange={(e) => setFormData({ ...formData, pincode: e.target.value })} 
                placeholder="Enter pincode"
              />
            </div>

            <div className="space-y-2">
              <Label>Occasion</Label>
              <Select value={formData.occasion || 'NONE'} onValueChange={(value) => setFormData({ ...formData, occasion: value === 'NONE' ? '' : value })}>
                <SelectTrigger data-testid="occasion-select">
                  <SelectValue placeholder="Select occasion (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">None</SelectItem>
                  <SelectItem value="Special Issue">Special Issue</SelectItem>
                  <SelectItem value="15th August Special Issue">15th August Special Issue</SelectItem>
                  <SelectItem value="26th January Special Issue">26th January Special Issue</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Start Month and Year */}
            <div className="space-y-2">
              <Label>Start Month</Label>
              <Select value={formData.start_month} onValueChange={(value) => setFormData({ ...formData, start_month: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="January">January</SelectItem>
                  <SelectItem value="February">February</SelectItem>
                  <SelectItem value="March">March</SelectItem>
                  <SelectItem value="April">April</SelectItem>
                  <SelectItem value="May">May</SelectItem>
                  <SelectItem value="June">June</SelectItem>
                  <SelectItem value="July">July</SelectItem>
                  <SelectItem value="August">August</SelectItem>
                  <SelectItem value="September">September</SelectItem>
                  <SelectItem value="October">October</SelectItem>
                  <SelectItem value="November">November</SelectItem>
                  <SelectItem value="December">December</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Year</Label>
              <Select value={formData.start_year} onValueChange={(value) => setFormData({ ...formData, start_year: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent className="max-h-72 overflow-y-auto">
                  {Array.from({ length: 25 }, (_, i) => (2021 + i).toString()).map(y => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* End Month and Year */}
            <div className="space-y-2">
              <Label>End Month</Label>
              <Select value={formData.end_month} onValueChange={(value) => setFormData({ ...formData, end_month: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="January">January</SelectItem>
                  <SelectItem value="February">February</SelectItem>
                  <SelectItem value="March">March</SelectItem>
                  <SelectItem value="April">April</SelectItem>
                  <SelectItem value="May">May</SelectItem>
                  <SelectItem value="June">June</SelectItem>
                  <SelectItem value="July">July</SelectItem>
                  <SelectItem value="August">August</SelectItem>
                  <SelectItem value="September">September</SelectItem>
                  <SelectItem value="October">October</SelectItem>
                  <SelectItem value="November">November</SelectItem>
                  <SelectItem value="December">December</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>End Year</Label>
              <Select value={formData.end_year} onValueChange={(value) => setFormData({ ...formData, end_year: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent className="max-h-72 overflow-y-auto">
                  {Array.from({ length: 25 }, (_, i) => (2021 + i).toString()).map(y => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Advertisement fields - Only show for Advertiser type */}
            {formData.type === 'Advertiser' && (
              <>
                <div className="space-y-2">
                  <Label>Advertisement Position</Label>
                  <Select value={formData.advertisement_position} onValueChange={(value) => setFormData({ ...formData, advertisement_position: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select position" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Front Cover Inside">Front Cover Inside</SelectItem>
                      <SelectItem value="Full Page Inside">Full Page Inside</SelectItem>
                      <SelectItem value="Full Page">Full Page</SelectItem>
                      <SelectItem value="Back Cover Inside">Back Cover Inside</SelectItem>
                      <SelectItem value="Back Cover">Back Cover</SelectItem>
                      <SelectItem value="Half Page">Half Page</SelectItem>
                      <SelectItem value="Bottom Strip">Bottom Strip</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Advertisement Tenure</Label>
                  <Select value={formData.advertisement_tenure} onValueChange={(value) => setFormData({ ...formData, advertisement_tenure: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select tenure" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1 Month">1 Month</SelectItem>
                      <SelectItem value="2 Months">2 Months</SelectItem>
                      <SelectItem value="3 Months">3 Months</SelectItem>
                      <SelectItem value="4 Months">4 Months</SelectItem>
                      <SelectItem value="5 Months">5 Months</SelectItem>
                      <SelectItem value="6 Months">6 Months</SelectItem>
                      <SelectItem value="1 Year">1 Year</SelectItem>
                      <SelectItem value="Alternate Months">Alternate Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Period/Duration - Different options based on type */}
            <div className="space-y-2">
              <Label>Period/Duration</Label>
              <Select value={formData.advertisement_tenure} onValueChange={(value) => setFormData({ ...formData, advertisement_tenure: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select period/duration" />
                </SelectTrigger>
                <SelectContent>
                  {formData.type === 'Member' ? (
                    <>
                      <SelectItem value="1 Year">1 Year</SelectItem>
                      <SelectItem value="2 Years">2 Years</SelectItem>
                      <SelectItem value="3 Years">3 Years</SelectItem>
                      <SelectItem value="Lifetime">Lifetime</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="1 Month">1 Month</SelectItem>
                      <SelectItem value="2 Months">2 Months</SelectItem>
                      <SelectItem value="3 Months">3 Months</SelectItem>
                      <SelectItem value="4 Months">4 Months</SelectItem>
                      <SelectItem value="5 Months">5 Months</SelectItem>
                      <SelectItem value="6 Months">6 Months</SelectItem>
                      <SelectItem value="1 Year">1 Year</SelectItem>
                      <SelectItem value="Alternate Months">Alternate Months</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Item Description */}
            <div className="space-y-2 md:col-span-2">
              <Label>Item Description (Optional)</Label>
              <Textarea
                value={formData.item_description}
                onChange={(e) => {
                  setUserEditedDescription(true);
                  setFormData({ ...formData, item_description: e.target.value });
                }}
                placeholder={`Default: Towards your ${formData.product || 'Subscription'} charges of ${formData.magazine ? formData.magazine + ' magazine' : 'Magazine'} for ${formData.advertisement_tenure || '1 Month'}`}
                rows={2}
              />
              <p className="text-xs text-gray-500">
                {userEditedDescription
                  ? 'Custom description — will not be auto-overwritten. '
                  : 'Default auto-fills until you edit it. '}
                <button
                  type="button"
                  data-testid="reset-description-btn"
                  className="text-blue-600 underline ml-1 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    const product = formData.product || 'Subscription';
                    const mag = formData.magazine ? formData.magazine + ' magazine' : 'Magazine';
                    const tenure = formData.advertisement_tenure || '1 Month';
                    const defaultDesc = `Towards your ${product} charges of ${mag} for ${tenure}`;
                    setFormData(prev => ({ ...prev, item_description: defaultDesc }));
                    setUserEditedDescription(false);
                  }}
                >Reset to default</button>
              </p>
            </div>

            {/* Include in Aabkari checkbox - Only for Sugar Times Advertisers */}
            {formData.magazine === 'Sugar Times' && formData.type === 'Advertiser' && (
              <div className="space-y-2 md:col-span-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="include_in_aabkari"
                    checked={formData.include_in_aabkari}
                    onChange={(e) => setFormData({ ...formData, include_in_aabkari: e.target.checked })}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <Label htmlFor="include_in_aabkari" className="cursor-pointer font-semibold">
                    Include advertisement in Aabkari Times also (Complimentary Offer)
                  </Label>
                </div>
                <p className="text-xs text-gray-500 ml-6">This will mark the advertiser as having cross-magazine offer</p>
              </div>
            )}

            {/* GST Rate Override + HSN/SAC code */}
            <div className="space-y-2">
              <Label>GST Rate</Label>
              <Select
                value={formData.gst_rate || 'auto'}
                onValueChange={(value) => {
                  const next = recalcAll({
                    ...formData,
                    gst_rate: value,
                    gross_amount: parseFloat(formData.gross_amount) || 0,
                    discount_amount: parseFloat(formData.discount_amount) || 0,
                    total_amount: parseFloat(formData.total_amount) || 0,
                    last_edited_field: formData.last_edited_field || 'gross',
                  });
                  setFormData({
                    ...formData,
                    gst_rate: value,
                    sgst_amount: next.sgst_amount,
                    cgst_amount: next.cgst_amount,
                    igst_amount: next.igst_amount,
                    total_amount: next.total_amount?.toString?.() ?? next.total_amount,
                  });
                }}
              >
                <SelectTrigger data-testid="gst-rate-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (by product)</SelectItem>
                  <SelectItem value="0">0% (No GST)</SelectItem>
                  <SelectItem value="5">5%</SelectItem>
                  <SelectItem value="18">18%</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Effective rate: {(getEffectiveGstRate(formData) * 100).toFixed(0)}%
              </p>
            </div>

            <div className="space-y-2">
              <Label>HSN / SAC Code</Label>
              <Input
                value={formData.hsn_sac || ''}
                onChange={(e) => setFormData({ ...formData, hsn_sac: e.target.value })}
                placeholder="998363 / 998397"
                data-testid="hsn-sac-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Gross Amount *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.gross_amount}
                onChange={(e) => {
                  const next = recalcAll({
                    ...formData,
                    gross_amount: parseFloat(e.target.value) || 0,
                    discount_amount: parseFloat(formData.discount_amount) || 0,
                    total_amount: parseFloat(formData.total_amount) || 0,
                    last_edited_field: 'gross',
                  });
                  setFormData({
                    ...formData,
                    gross_amount: e.target.value,
                    discount_amount: next.discount_amount?.toString?.() ?? next.discount_amount,
                    discount_percentage: next.discount_percentage?.toString?.() ?? next.discount_percentage,
                    sgst_amount: next.sgst_amount,
                    cgst_amount: next.cgst_amount,
                    igst_amount: next.igst_amount,
                    total_amount: (next.total_amount ?? 0).toString(),
                    last_edited_field: 'gross',
                  });
                }}
                placeholder="Enter gross amount"
                data-testid="gross-amount-input"
              />
              <p className="text-xs text-gray-500">GST will be added on top of (Gross − Discount)</p>
            </div>

            <div className="space-y-2">
              <Label>Discount % (Optional)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.discount_percentage}
                onChange={(e) => {
                  const pct = e.target.value;
                  const gross = parseFloat(formData.gross_amount) || 0;
                  const discAmt = pct && gross ? Math.round(gross * parseFloat(pct) / 100 * 100) / 100 : 0;
                  const next = recalcAll({
                    ...formData,
                    gross_amount: gross,
                    discount_amount: discAmt,
                    discount_percentage: parseFloat(pct) || 0,
                    last_edited_field: 'discount',
                  });
                  setFormData({
                    ...formData,
                    discount_percentage: pct,
                    discount_amount: discAmt.toString(),
                    sgst_amount: next.sgst_amount,
                    cgst_amount: next.cgst_amount,
                    igst_amount: next.igst_amount,
                    total_amount: (next.total_amount ?? 0).toString(),
                    last_edited_field: 'discount',
                  });
                }}
                placeholder="e.g. 20"
              />
            </div>

            <div className="space-y-2">
              <Label>Discount Amount</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.discount_amount}
                onChange={(e) => {
                  const discAmt = e.target.value;
                  const gross = parseFloat(formData.gross_amount) || 0;
                  const pct = discAmt && gross ? Math.round(parseFloat(discAmt) / gross * 100 * 100) / 100 : 0;
                  const next = recalcAll({
                    ...formData,
                    gross_amount: gross,
                    discount_amount: parseFloat(discAmt) || 0,
                    discount_percentage: pct,
                    last_edited_field: 'discount',
                  });
                  setFormData({
                    ...formData,
                    discount_amount: discAmt,
                    discount_percentage: pct.toString(),
                    sgst_amount: next.sgst_amount,
                    cgst_amount: next.cgst_amount,
                    igst_amount: next.igst_amount,
                    total_amount: (next.total_amount ?? 0).toString(),
                    last_edited_field: 'discount',
                  });
                }}
                placeholder="Auto-calculated or enter"
                className="bg-gray-50"
              />
            </div>

            {/* GST Breakdown (visible whenever effective rate > 0) */}
            {getEffectiveGstRate(formData) > 0 && (
              <>
                <div className="space-y-2">
                  <Label>SGST</Label>
                  <Input type="number" value={Number(formData.sgst_amount || 0).toFixed(2)} disabled className="bg-gray-100" />
                </div>
                <div className="space-y-2">
                  <Label>CGST</Label>
                  <Input type="number" value={Number(formData.cgst_amount || 0).toFixed(2)} disabled className="bg-gray-100" />
                </div>
                <div className="space-y-2">
                  <Label>IGST</Label>
                  <Input type="number" value={Number(formData.igst_amount || 0).toFixed(2)} disabled className="bg-gray-100" />
                </div>
              </>
            )}

            <div className="space-y-2 md:col-span-2">
              <Label className="text-lg font-bold text-green-700">Grand Total (Inclusive of GST)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.total_amount}
                onChange={(e) => {
                  const next = recalcAll({
                    ...formData,
                    gross_amount: parseFloat(formData.gross_amount) || 0,
                    discount_amount: parseFloat(formData.discount_amount) || 0,
                    total_amount: parseFloat(e.target.value) || 0,
                    last_edited_field: 'total',
                  });
                  setFormData({
                    ...formData,
                    gross_amount: (next.gross_amount ?? 0).toString(),
                    discount_percentage: next.discount_percentage?.toString?.() ?? next.discount_percentage,
                    sgst_amount: next.sgst_amount,
                    cgst_amount: next.cgst_amount,
                    igst_amount: next.igst_amount,
                    total_amount: e.target.value,
                    last_edited_field: 'total',
                  });
                }}
                placeholder="Type inclusive total to reverse-calc Gross & GST"
                className="font-bold text-xl bg-green-50 border-green-300 text-green-800"
                data-testid="total-amount-input"
              />
              <p className="text-xs text-gray-500">Type here for GST-inclusive: Taxable = Total / (1 + Rate). Gross & GST will reverse-calculate.</p>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Amount in Words</Label>
              <Input
                value={(() => {
                  const t = parseFloat(formData.total_amount) || 0;
                  return t > 0 ? numberToWords(t) : '';
                })()}
                disabled
                className="bg-gray-100 font-semibold"
              />
            </div>
          </div>

          <div className="mt-6 space-y-2">
            <Label>Terms & Conditions</Label>
            <Textarea value={formData.terms_conditions} onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })} rows={5} />
          </div>

          <div className="mt-8 flex justify-end">
            <Button type="submit" disabled={loading} className="btn-primary" style={{ backgroundColor: '#059669' }} data-testid="submit-button">
              <Save className="mr-2" size={18} />
              {loading ? 'Creating...' : 'CREATE PROFORMA INVOICE'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProformaInvoice;
